import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { CryptoService } from '../services/crypto.service';
import {
  PlatformTaskInterfaceContext,
  PlatformTaskInterfaceStateService,
} from '../services/platform-task-interface-state.service';
import { PLATFORM_CHAT_ENTITY_TYPE } from '../components/platform-comments-chat/platform-chat-entity.const';
import { contentUnavailableTree } from '../utils/content-unavailable.utils';
import { ProjectV2ServiceService } from '../services/project-v2-service.service';
import { Brand, DriveCampaign, WorkspaceDTO } from '../interfaces';
import { Observable, catchError, forkJoin, of, switchMap, tap } from 'rxjs';
import { WorkspaceService } from '../services/workspace.service';
import { AuthService } from '../services/auth.service';
import { TimelineService } from '../services/timeline.service';
import { brandHasWorkspace } from '../utils/brand-workspace.utils';

export const canAccessCampaignTask: CanActivateFn = (route, state) => {
  const router: Router = inject(Router);
  const cryptoService: CryptoService = inject(CryptoService);
  const taskStateService: PlatformTaskInterfaceStateService = inject(PlatformTaskInterfaceStateService);
  const projectV2Service: ProjectV2ServiceService = inject(ProjectV2ServiceService);
  const workspaceService: WorkspaceService = inject(WorkspaceService);
  const authService: AuthService = inject(AuthService);
  const timelineService: TimelineService = inject(TimelineService);
  const { workspaceId, campaignId } = route.params;

  if (!campaignId) {
    return contentUnavailableTree(router);
  }

  const actualWorkspaceId: string = cryptoService.decryptUsingAES256(workspaceId);
  const actualCampaignId: string = cryptoService.decryptUsingAES256(campaignId);

  if (!actualCampaignId) {
    return contentUnavailableTree(router);
  }

  const context: PlatformTaskInterfaceContext = taskStateService.getCurrentValue();

  if (context.pageId === actualCampaignId && context.entityType === PLATFORM_CHAT_ENTITY_TYPE.CAMPAIGN) {
    if (timelineService.driveCampaignData?.id !== actualCampaignId) {
      timelineService.driveCampaignData = null;
    }
    return true;
  }

  const hasActiveWorkspace: boolean = workspaceService.getActiveWorkspace() !== null;
  const userBrand: Brand | undefined = authService.getUserBrand();

  // The workspace may belong to another brand (e.g. a bookmarked URL after a brand switch);
  // proceeding would set an undefined active workspace.
  if (!hasActiveWorkspace && !brandHasWorkspace(userBrand, actualWorkspaceId)) {
    return contentUnavailableTree(router);
  }

  const brandWorkspace$: Observable<WorkspaceDTO | null> = hasActiveWorkspace
    ? of(null)
    : workspaceService.getWorkspaceById(userBrand.companyId, actualWorkspaceId);

  return forkJoin([brandWorkspace$, projectV2Service.getSingleDriveCampaign(actualWorkspaceId, actualCampaignId)]).pipe(
    tap(([brandWorkspace, campaign]: [WorkspaceDTO | null, DriveCampaign]) => {
      if (brandWorkspace) {
        workspaceService.setActiveWorkspace(brandWorkspace);
      }
      taskStateService.setContext({
        pageId: actualCampaignId,
        entityType: PLATFORM_CHAT_ENTITY_TYPE.CAMPAIGN,
        entityName: campaign.name,
      });
      timelineService.activeCampaign$.next(campaign);
      timelineService.driveCampaignData = campaign;
    }),
    switchMap(() => {
      return of(true);
    }),
    catchError(() => of(contentUnavailableTree(router)))
  );
};
